import { useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Badge } from '@tailwindComponents/Badge';
import { Button } from '@tailwindComponents/Button';
import { IntegrationConfig } from './integrationsConfig';
import {
  IntegrationCredential,
  isEbay,
  reconnectDeadlineOf,
} from './integrationsHelpers';

interface IntegrationCardProps {
  config: IntegrationConfig;
  connectedCredential?: IntegrationCredential;
  isBusy?: boolean;
  isReconnectAvailable?: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}

function AccessLostBadge({ label }: { label: string }) {
  return (
    <span className='inline-flex shrink-0 items-center rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20'>
      {label}
    </span>
  );
}

export function IntegrationCard({
  config,
  connectedCredential,
  isBusy = false,
  isReconnectAvailable = true,
  onConnect,
  onDisconnect,
}: IntegrationCardProps) {
  const [logoError, setLogoError] = useState(false);
  const isConnected = Boolean(connectedCredential);
  const connectedAccountUsername =
    connectedCredential?.connectedAccountUsername;
  const isReconnectNeeded = connectedCredential?.isReconnectNeeded === true;
  const reconnectDeadline = isReconnectNeeded
    ? null
    : reconnectDeadlineOf(connectedCredential?.reconnectBy);
  const isAccessLost =
    isReconnectNeeded || reconnectDeadline?.status === 'expired';
  const isConnectionStatusUnknown =
    !isAccessLost && connectedCredential?.isReconnectCheckUnavailable === true;

  const renderConnectionBadge = () => {
    if (isReconnectNeeded) {
      return <AccessLostBadge label='Reconnect needed' />;
    }
    if (reconnectDeadline?.status === 'expired') {
      return <AccessLostBadge label='Expired' />;
    }
    if (isConnectionStatusUnknown) {
      return (
        <Badge
          intent='warning'
          label='Status unknown'
          className='shrink-0 px-2.5 ring-1 ring-inset ring-amber-600/20'
        />
      );
    }
    if (isConnected) {
      return (
        <span className='inline-flex shrink-0 items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20'>
          <CheckCircleIcon className='h-3.5 w-3.5' />
          Connected
        </span>
      );
    }
    return (
      <span className='inline-flex shrink-0 items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/20'>
        Not connected
      </span>
    );
  };

  return (
    <div className='flex w-[280px] flex-col rounded-lg border border-gray-200 bg-white transition-all duration-200 hover:border-gray-300 hover:shadow-lg'>
      {/* Logo */}
      <div className='flex h-[120px] items-center justify-center border-b border-gray-100 bg-gray-50 p-6'>
        {config.logo && !logoError ? (
          <img
            src={config.logo}
            alt={config.name}
            onError={() => setLogoError(true)}
            className='max-h-full max-w-[70%] object-contain'
          />
        ) : (
          <div className='flex h-14 w-14 items-center justify-center rounded-lg bg-blue-50 text-2xl font-semibold text-primary'>
            {config.name.charAt(0)}
          </div>
        )}
      </div>

      {/* Info */}
      <div className='flex flex-1 flex-col gap-2 p-4'>
        <div className='flex items-start justify-between gap-2'>
          <h3 className='text-base font-semibold text-gray-900'>
            {config.name}
          </h3>
          {renderConnectionBadge()}
        </div>
        <span className='text-xs font-medium uppercase tracking-wide text-gray-400'>
          {config.category}
        </span>
        <p className='text-sm text-gray-500'>{config.description}</p>
        {connectedAccountUsername && (
          <p className='break-all text-sm font-medium text-gray-900'>
            Connected as {connectedAccountUsername}
          </p>
        )}
        {isReconnectNeeded && (
          <p className='text-xs font-medium text-red-700'>
            {config.name} access was revoked — reconnect
          </p>
        )}
        {reconnectDeadline?.status === 'expired' && (
          <p className='text-xs font-medium text-red-700'>
            {config.name} access expired — reconnect
          </p>
        )}
        {isConnectionStatusUnknown && (
          <p className='text-xs font-medium text-amber-700'>
            Flox couldn&apos;t check your {config.name} access just now —
            refresh to check again
          </p>
        )}
        {reconnectDeadline?.status === 'endsSoon' && (
          <p className='text-xs font-medium text-amber-700'>
            Reconnect soon — {config.name} access ends {reconnectDeadline.date}
          </p>
        )}
        {reconnectDeadline?.status === 'endsLater' && (
          <p className='text-xs text-gray-500'>
            Reconnect by {reconnectDeadline.date}
          </p>
        )}

        {/* Action */}
        <div className='mt-2 flex flex-wrap gap-2'>
          {isConnected ? (
            <>
              {isEbay(config) && isReconnectAvailable && (
                <Button
                  label='Reconnect'
                  aria-label={`Reconnect ${config.name}`}
                  intent='secondary'
                  size='sm'
                  disabled={isBusy}
                  onClick={onConnect}
                />
              )}
              <Button
                label='Disconnect'
                aria-label={`Disconnect ${config.name}`}
                intent='danger'
                size='sm'
                disabled={isBusy}
                onClick={onDisconnect}
              />
            </>
          ) : (
            <Button
              label='Connect'
              aria-label={`Connect ${config.name}`}
              intent='primary'
              size='sm'
              disabled={isBusy}
              onClick={onConnect}
            />
          )}
        </div>
      </div>
    </div>
  );
}
